import { Component, type ErrorInfo, type ReactNode } from 'react'
import { ArrowLeft, ChevronRight, Home, RefreshCw } from 'lucide-react'

type ErrorPageProps = {
  code: string
  title?: string
  message?: string
  canRetry?: boolean
}

type ErrorCopy = {
  backToVectria: string
  retry: string
  home: string
  previous: string
  notFoundTitle: string
  notFoundMessage: string
  serverTitle: string
  serverMessage: string
}

const errorCopy: Record<string, ErrorCopy> = {
  en: { backToVectria: 'Back to VECTRIA', retry: 'Reload', home: 'Back to Studio', previous: 'Previous page', notFoundTitle: 'Page not found', notFoundMessage: 'This address is not available in VECTRIA Studio. Return to your workspace to continue.', serverTitle: 'Studio encountered a problem', serverMessage: 'The interface could not be loaded. Work saved in your browser will not be deleted.' },
  id: { backToVectria: 'Kembali ke VECTRIA', retry: 'Muat ulang', home: 'Kembali ke Studio', previous: 'Halaman sebelumnya', notFoundTitle: 'Halaman tidak ditemukan', notFoundMessage: 'Alamat ini tidak tersedia di VECTRIA Studio. Kembali ke ruang kerja untuk melanjutkan.', serverTitle: 'Studio mengalami gangguan', serverMessage: 'Antarmuka gagal dimuat. Pekerjaan yang tersimpan di browser tidak akan dihapus.' },
  'zh-cn': { backToVectria: '返回 VECTRIA', retry: '重新加载', home: '返回 Studio', previous: '返回上一页', notFoundTitle: '找不到页面', notFoundMessage: 'VECTRIA Studio 中不存在此地址。请返回工作区继续操作。', serverTitle: 'Studio 出现问题', serverMessage: '界面无法加载。保存在浏览器中的工作不会被删除。' },
  ja: { backToVectria: 'VECTRIA に戻る', retry: '再読み込み', home: 'Studio に戻る', previous: '前のページ', notFoundTitle: 'ページが見つかりません', notFoundMessage: 'この URL は VECTRIA Studio に存在しません。ワークスペースに戻って続行してください。', serverTitle: 'Studio で問題が発生しました', serverMessage: '画面を読み込めませんでした。ブラウザに保存されている作業内容は削除されません。' },
  ko: { backToVectria: 'VECTRIA로 돌아가기', retry: '새로고침', home: 'Studio로 돌아가기', previous: '이전 페이지', notFoundTitle: '페이지를 찾을 수 없습니다', notFoundMessage: 'VECTRIA Studio에 없는 주소입니다. 작업 공간으로 돌아가 계속하세요.', serverTitle: 'Studio에 문제가 발생했습니다', serverMessage: '인터페이스를 불러오지 못했습니다. 브라우저에 저장된 작업은 삭제되지 않습니다.' },
  es: { backToVectria: 'Volver a VECTRIA', retry: 'Recargar', home: 'Volver a Studio', previous: 'Página anterior', notFoundTitle: 'Página no encontrada', notFoundMessage: 'Esta dirección no está disponible en VECTRIA Studio. Vuelve a tu espacio de trabajo para continuar.', serverTitle: 'Studio encontró un problema', serverMessage: 'No se pudo cargar la interfaz. El trabajo guardado en tu navegador no se eliminará.' },
  'pt-br': { backToVectria: 'Voltar para a VECTRIA', retry: 'Recarregar', home: 'Voltar ao Studio', previous: 'Página anterior', notFoundTitle: 'Página não encontrada', notFoundMessage: 'Este endereço não está disponível no VECTRIA Studio. Volte ao seu espaço de trabalho para continuar.', serverTitle: 'O Studio encontrou um problema', serverMessage: 'Não foi possível carregar a interface. O trabalho salvo no navegador não será excluído.' },
  ru: { backToVectria: 'Вернуться в VECTRIA', retry: 'Перезагрузить', home: 'Вернуться в Studio', previous: 'Предыдущая страница', notFoundTitle: 'Страница не найдена', notFoundMessage: 'Этот адрес недоступен в VECTRIA Studio. Вернитесь в рабочее пространство, чтобы продолжить.', serverTitle: 'В Studio возникла проблема', serverMessage: 'Не удалось загрузить интерфейс. Работа, сохранённая в браузере, не будет удалена.' },
  tr: { backToVectria: 'VECTRIA’ya Dön', retry: 'Yeniden Yükle', home: 'Studio’ya Dön', previous: 'Önceki sayfa', notFoundTitle: 'Sayfa bulunamadı', notFoundMessage: 'Bu adres VECTRIA Studio’da mevcut değil. Devam etmek için çalışma alanınıza dönün.', serverTitle: 'Studio bir sorunla karşılaştı', serverMessage: 'Arayüz yüklenemedi. Tarayıcınıza kaydedilen çalışmalar silinmeyecek.' },
  vi: { backToVectria: 'Quay lại VECTRIA', retry: 'Tải lại', home: 'Quay lại Studio', previous: 'Trang trước', notFoundTitle: 'Không tìm thấy trang', notFoundMessage: 'Địa chỉ này không tồn tại trong VECTRIA Studio. Hãy quay lại không gian làm việc để tiếp tục.', serverTitle: 'Studio gặp sự cố', serverMessage: 'Không thể tải giao diện. Công việc đã lưu trong trình duyệt sẽ không bị xóa.' },
  th: { backToVectria: 'กลับไปที่ VECTRIA', retry: 'โหลดใหม่', home: 'กลับไปที่ Studio', previous: 'หน้าก่อนหน้า', notFoundTitle: 'ไม่พบหน้านี้', notFoundMessage: 'ไม่มีที่อยู่นี้ใน VECTRIA Studio โปรดกลับไปยังพื้นที่ทำงานเพื่อดำเนินการต่อ', serverTitle: 'Studio พบปัญหา', serverMessage: 'ไม่สามารถโหลดอินเทอร์เฟซได้ งานที่บันทึกไว้ในเบราว์เซอร์จะไม่ถูกลบ' },
  ar: { backToVectria: 'العودة إلى VECTRIA', retry: 'إعادة التحميل', home: 'العودة إلى Studio', previous: 'الصفحة السابقة', notFoundTitle: 'الصفحة غير موجودة', notFoundMessage: 'هذا العنوان غير متاح في VECTRIA Studio. عُد إلى مساحة عملك للمتابعة.', serverTitle: 'واجه Studio مشكلة', serverMessage: 'تعذر تحميل الواجهة. لن يُحذف العمل المحفوظ في متصفحك.' },
}

function currentErrorCopy() {
  const pathLocale = window.location.pathname.split('/').filter(Boolean)[0]?.toLowerCase()
  const storedLocale = window.localStorage.getItem('vectria_locale')?.toLowerCase()
  const locale = pathLocale === 'zh' ? 'zh-cn' : pathLocale === 'pt' ? 'pt-br' : pathLocale === 'ru-ru' ? 'ru' : pathLocale === 'tr-tr' ? 'tr' : pathLocale === 'vi-vn' ? 'vi' : pathLocale === 'th-th' ? 'th' : pathLocale === 'ar-sa' ? 'ar' : pathLocale
  return errorCopy[locale ?? ''] ?? errorCopy[storedLocale ?? ''] ?? errorCopy.en
}
function goToPreviousPage() {
  const referrer = document.referrer;
  if (window.history.length > 1 && referrer) {
    try {
      if (new URL(referrer).origin === window.location.origin) {
        window.history.back();
        return;
      }
    } catch {}
  }
  window.location.assign('/');
}
export function ErrorPage({ code, title, message, canRetry = false }: ErrorPageProps) {
  const copy = currentErrorCopy()
  const isNotFound = code === '404'

  return (
    <main className={`error-page ${isNotFound ? 'error-page-not-found' : 'error-page-server'}`} aria-labelledby="error-title">
      <section className="error-card">
        <a className="error-brand" href="/" aria-label={copy.backToVectria}>
          <img className="error-brand-emblem" src="/emblem.webp" width="32" height="32" decoding="async" alt="" />
          <span className="brand-wordmark error-brand-wordmark">VECTRIA</span>
        </a>

        {!isNotFound && (
          <div className="error-status-artwork" aria-label={`Error ${code}`}>{code}</div>
        )}

        <div className="error-content">
          <h1 id="error-title">{title ?? (isNotFound ? copy.notFoundTitle : copy.serverTitle)}</h1>
          <p className="error-message">{message ?? (isNotFound ? copy.notFoundMessage : copy.serverMessage)}</p>
          <div className="error-actions">
            {canRetry ? (
              <button id="error-retry-button" className="error-button error-button-primary" onClick={() => window.location.reload()}>
                <RefreshCw size={17} /> {copy.retry} <ChevronRight size={16} />
              </button>
            ) : (
              <a id="error-home-button" className="error-button error-button-primary" href="/">
                <Home size={17} /> {copy.home} <ChevronRight size={16} />
              </a>
            )}
            <button id="error-back-button" className="error-button error-button-secondary" onClick={goToPreviousPage}>
              <ArrowLeft size={17} /> {copy.previous}
            </button>
          </div>
        </div>
      </section>
    </main>
  )
}

type BoundaryState = { hasError: boolean }

export class AppErrorBoundary extends Component<{ children: ReactNode }, BoundaryState> {
  state: BoundaryState = { hasError: false }

  static getDerivedStateFromError(): BoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Vectria UI render failure', error, info.componentStack)
  }

  render() {
    if (this.state.hasError) {
      return <ErrorPage code="500" canRetry />
    }
    return this.props.children
  }
}

const localeAliases = new Set(['en', 'id', 'es', 'pt', 'pt-br', 'ru', 'ru-ru', 'tr', 'tr-tr', 'vi', 'vi-vn', 'th', 'th-th', 'ar', 'ar-sa', 'zh', 'zh-cn', 'ja', 'ko'])

export function isKnownFrontendPath(pathname: string) {
  const segments = pathname.split('/').filter(Boolean).map(segment => segment.toLowerCase())
  const routeSegments = localeAliases.has(segments[0] ?? '') ? segments.slice(1) : segments
  if (routeSegments.length === 0) return true
  if (routeSegments[0] === 'blog' || routeSegments[0] === 'tutorials' || routeSegments[0] === 'changelog') {
    return routeSegments.length <= 2
  }
  if (routeSegments[0] !== 'settings' || routeSegments.length > 2) return false
  const hasAuth = Boolean(
    typeof window !== 'undefined' && (
      window.localStorage?.getItem('vectria_auth_token') ||
      window.sessionStorage?.getItem('vectria_auth_token')
    )
  )
  if (!hasAuth) return false
  return routeSegments.length === 1 || new Set(['profile', 'security', 'preferences', 'byok', 'workspace', 'billing', 'privacy']).has(routeSegments[1])
}
